import React, { useState } from 'react';
import { ControlledModal } from '@instinet-analytics/semantic-ui-components';
import { useMakeRequest } from 'utils/hooks/request';
import { textUtils, useInputError } from '@instinet-analytics/core-lib';
import './ShareLayoutModal.scss';
import { shareLayoutAction } from 'layoutManagement/store/thunks';
import { Layout } from 'layout/LayoutState';
import { useLayoutsList } from 'layout/store/useLayoutsList';
import ShareLayoutContent from 'layoutManagement/ShareLayoutContent';
import { useDispatchShowInfo } from 'notification/store/dispatcher';
import { useAppLayoutOrigin } from 'layout/WellKnownLayoutOrigin';

const { isFalsyOrWhitespace } = textUtils;

interface ShareLayoutModalProps {
    isOpen: boolean;
    activeLayout: Layout;
    onActionCompleted: () => void;
}

export default function ShareLayoutModal(props: ShareLayoutModalProps) {
    const {
        isOpen,
        onActionCompleted: onActionCompletedProps,
        activeLayout,
    } = props;

    const { id: activeLayoutId, name: activeLayoutName } = activeLayout;

    const makeRequest = useMakeRequest();
    const [shareLayoutUsername, setShareLayoutUsername] = useState('');
    const dispatchShowInfo = useDispatchShowInfo();
    const appLayoutOrigin = useAppLayoutOrigin();
    const { forceLayoutListReload } = useLayoutsList(appLayoutOrigin);

    const {
        errorMessage,
        invalidInput,
        setInputTextAndClearInvalidState,
        clearError,
        displayError,
    } = useInputError();

    const reset = () => {
        clearError();
        setShareLayoutUsername('');
    };

    const onActionCompleted = () => {
        reset();
        onActionCompletedProps();
    };

    const shareLayout = () => {
        if (isFalsyOrWhitespace(shareLayoutUsername)) {
            setShareLayoutUsername('');
            displayError('Please enter a username');
            return;
        }

        clearError();

        makeRequest<[number, string], void>(shareLayoutAction)(
            activeLayoutId!,
            shareLayoutUsername
        )
            .then(() => {
                dispatchShowInfo(`Layout shared with ${shareLayoutUsername}`);
                forceLayoutListReload();
                onActionCompleted();
            })
            .catch((err) => {
                displayError('Error sharing layout', err);
            })
            .finally(() => {
                setShareLayoutUsername('');
            });
    };

    return (
        <ControlledModal
            className="ShareLayoutModal"
            headerText="Share Layout"
            saveText="Share"
            isOpen={isOpen}
            onCancel={onActionCompleted}
            onSave={shareLayout}
        >
            <ShareLayoutContent
                layoutName={activeLayoutName}
                username={shareLayoutUsername}
                onUsernameChanged={setInputTextAndClearInvalidState(
                    setShareLayoutUsername
                )}
                onEnter={shareLayout}
                error={errorMessage}
                isInvalid={invalidInput}
            />
        </ControlledModal>
    );
}
